<template>
  <c-input
    v-bind="$attrs"
    ref="inputRef"
    v-model="model"
    type="time"
    :rules
    hide-bottom-space
    :error="errorMessage !== undefined"
    :error-message="errorMessage"
    @blur="emitBlur"
  >
    <template #append>
      <q-icon size="xs" color="primary" :name="toDuotoneIcon(inputTimeIcon)" />
    </template>
  </c-input>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  useTemplateRef,
  useAttrs,
  onBeforeMount,
} from "vue";
import { isSecondTimeLater } from "shared/utils/dateHelpers";
import { inputTimeIcon, toDuotoneIcon } from "common/defaultIcons";
import { cloneDeep } from "lodash";

// Define the model
const model = defineModel({
  required: true,
  type: [String, null],
  default: () => "",
});

// Props
const props = defineProps({
  min: {
    type: [String, null],
    default: null,
  },
  defaultTime: {
    type: [String, null],
    default: null,
  },

  required: {
    type: Boolean,
    default: false,
  },
});

// Set refs
const errorMessage = ref<string | undefined>(undefined);
const emit = defineEmits({
  blur(value: { value: string | null; hasChanged: boolean }): void {},
});

const initialValue = ref<string | null>(null);

const emitBlur = () => {
  emit("blur", { value: model.value, hasChanged: hasChanged.value });

  initialValue.value = model.value;
};

const hasChanged = computed(() => initialValue.value !== model.value);

// Ref
const inputRef = useTemplateRef("inputRef");
const attrs = useAttrs();
const isDisabled = computed(() => {
  const disabled = attrs?.disabled;
  const disable = attrs?.disable;
  return (
    disabled === "" || disabled === true || disable === "" || disable === true
  );
});

// Compute the effective minimum time, optionally adding the minimal gap, and ensure HH:mm padding
const computeMinToCompare = () => {
  if (props.min === null) {
    return null;
  }

  let base = props.min;

  if (!base) {
    return null;
  }

  const [h = "", m = ""] = String(base).split(":");
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const isValidTime = (time) => {
  if (isDisabled.value === true) {
    errorMessage.value = undefined;
    return true;
  }

  const minToCompare = computeMinToCompare();

  // Only validate when a time value is present; empty is handled by the required rule
  if (minToCompare !== null && time && !isSecondTimeLater(minToCompare, time)) {
    errorMessage.value = "Deze tijd is niet geldig";
    return false;
  }

  errorMessage.value = undefined;
  return true;
};

const rules = computed(() => {
  if (isDisabled.value === true) {
    return [];
  }
  return [
    ...(props.required ? [(val) => !!val || "Dit veld is verplicht"] : []),
    (val) => isValidTime(val) || "Deze tijd is niet geldig",
  ];
});

const focus = () => {
  if (!inputRef.value?.inputRef) {
    return;
  }

  inputRef.value.inputRef.focus();
};

// If a defaultTime is provided, set it only when the model is empty
watch(
  [() => props.defaultTime, () => model.value],
  ([defaultTime, currentValue]) => {
    if ((currentValue == null || currentValue === "") && defaultTime) {
      const [h = "", m = ""] = String(defaultTime).split(":");
      const normalized = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

      if (isValidTime(normalized)) {
        model.value = normalized;
        initialValue.value = cloneDeep(normalized);
      }
    }
  },
  { immediate: true },
);

watch(
  [() => props.min, () => model.value],
  () => {
    isValidTime(model.value);
  },
  { immediate: true },
);

onBeforeMount(() => {
  // This way we can check if the value has changed
  initialValue.value = cloneDeep(model.value);
});

defineExpose({ focus });
</script>

<style lang="scss">
.q-input {
  input[type="time"] {
    &::-webkit-calendar-picker-indicator {
      display: none;
    }
  }
}
</style>
